import { useState, useEffect } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, Funnel, UserPlus } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { EmployeeProfile } from "./EmployeeProfile";

export function EmployeeDirectory() {
  const [users, setUsers] = useKV<any[]>("employees", []);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  useEffect(() => {
    // Sync with API on mount
    const fetchUsers = async () => {
      try {
        const data = await api.getUsers();
        setUsers(data);
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };
    fetchUsers();
  }, [setUsers]);

  const filteredUsers = users.filter(user => {
    if (!user?.name) return false;
    return (
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
      (user.department?.toLowerCase().includes(searchQuery.toLowerCase()) || false)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
       <div className="relative flex-1 max-w-md">
           <MagnifyingGlass className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
           <Input 
             placeholder="Search by name, email, or department..." 
             className="pl-9 focus:ring-2 focus:ring-primary focus:ring-offset-2"
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
             aria-label="Search employees"
           />
         </div>
         <div className="flex gap-2">
           <Button variant="outline" className="gap-2" aria-label="Filter employees">
             <Funnel className="h-4 w-4" /> <span className="hidden sm:inline">Filter</span>
           </Button>
           <Button className="gap-2" aria-label="Add new employee">
             <UserPlus className="h-4 w-4" /> <span className="hidden sm:inline">Add Employee</span>
           </Button>
         </div>
      </div>

       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
         {filteredUsers.map((user) => (
           <Card 
             key={user.id} 
             className="hover:shadow-md transition-shadow cursor-pointer border-slate-200 dark:border-slate-800 focus-within:ring-2 focus-within:ring-primary"
             onClick={() => {
               setSelectedUserId(user.id);
               setIsProfileOpen(true);
             }}
             role="button"
             tabIndex={0}
             onKeyDown={(e) => {
               if (e.key === 'Enter' || e.key === ' ') {
                 setSelectedUserId(user.id);
                 setIsProfileOpen(true);
               }
             }}
             aria-label={`View ${user.name} profile`}
           >
             <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
               <Avatar className="h-20 w-20">
                 <AvatarImage src={user.avatar} alt={`${user.name} avatar`} />
                 <AvatarFallback className="text-xl">{user.name?.charAt(0) || '?'}</AvatarFallback>
               </Avatar>
               <div>
                 <h3 className="font-semibold text-lg truncate w-full">{user.name}</h3>
                 <p className="text-sm text-slate-600 dark:text-slate-400 truncate w-full">{user.role}</p>
               </div>
               <Badge variant="secondary" className="mt-2">
                 {user.department || "No Dept"}
               </Badge>
               <div className="text-xs text-slate-500 dark:text-slate-400 pt-2 w-full border-t mt-2 truncate">
                 {user.email}
               </div>
             </CardContent>
           </Card>
         ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No employees found matching your search.
        </div>
      )}

      <EmployeeProfile 
        userId={selectedUserId} 
        isOpen={isProfileOpen} 
        onClose={() => setIsProfileOpen(false)} 
      />
    </div>
  );
}
